import {Component, inject, effect, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {PackService} from '../../services/pack.service';
import {PageComponent} from '../shared/page/page.component';
import {EntityListComponent} from '../shared/entity-list/entity-list.component';
import {PromptsService} from '../../services/prompts.service';
import {ButtonComponent} from '../shared/button/button.component';
import {RouterLink} from '@angular/router';
import {OrganizationService} from '../../services/organization.service';
import {Observable, of} from 'rxjs';
import {EMPTY_LIST, PagedList} from '../../models/entity.model';
import {PackModel} from '../../models/pack.model';
import {PromptModel} from '../../models/prompt.model';

/**
 * Home page component showing recent packs and prompts from the active organization.
 * 
 * Waits for active organization to be loaded before fetching packs and prompts.
 * Re-fetches data when active organization changes.
 */
@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, PageComponent, EntityListComponent, ButtonComponent, RouterLink],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  host: {
    class: 'app-container'
  }
})
export class HomeComponent {
  private static readonly PAGE_SIZE = 5

  private readonly packService = inject(PackService)
  private readonly promptsService = inject(PromptsService)
  private readonly orgService = inject(OrganizationService)

  // Observables for packs and prompts - start with empty lists
  protected packs: Observable<PagedList<PackModel>> = of(EMPTY_LIST)
  protected prompts: Observable<PagedList<PromptModel>> = of(EMPTY_LIST)

  constructor() {
    // Load data when active organization changes
    effect(() => {
      const activeOrg = this.orgService.activeOrganization();
      if (activeOrg) {
        this.loadData();
      }
    });
  }

  /**
   * Load packs and prompts from the active organization.
   */
  private loadData(): void {
    this.packs = this.packService.list({ pageSize: HomeComponent.PAGE_SIZE });
    this.prompts = this.promptsService.list({ pageSize: HomeComponent.PAGE_SIZE });
  }
}
